// components/CompletePaymentModal.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../providers/AuthProvider';
import { useToast } from '../providers/ToastProvider';
import { AlertCircle, ArrowRight, Loader2, X } from 'lucide-react';

/**
 * Global modal triggered by authFetch's 403 REGISTRATION_PAYMENT_REQUIRED
 * interceptor. Mount once at the root (inside AuthProvider, inside ThemeProvider).
 */
const CompletePaymentModal = () => {
    const { colors } = useTheme();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const {
        unpaidGate,
        clearUnpaidGate,
        initiateRegistrationPayment,
        pollRegistrationStatus,
        refreshUser,
        user,
    } = useAuth();

    const [busy, setBusy] = useState(false);

    if (!unpaidGate?.active) return null;

    const handleComplete = async () => {
        setBusy(true);
        try {
            const res = await initiateRegistrationPayment();

            if (!res.ok) {
                showToast(res.error || 'Could not start payment.', 'error');
                setBusy(false);
                return;
            }

            const d = res.data || {};

            if (d.zero_fee) {
                await refreshUser();
                clearUnpaidGate();
                showToast('Registration completed. Welcome!', 'success');
                navigate('/');
                return;
            }

            if (d.already_paid) {
                showToast('Payment confirmed. Finishing setup…', 'info');
                const poll = await pollRegistrationStatus(user?.id);
                if (poll.ok) {
                    clearUnpaidGate();
                    showToast('Registration complete.', 'success');
                    navigate('/');
                } else {
                    showToast(
                        poll.error || 'Still confirming. Please check back shortly.',
                        'warning'
                    );
                }
                setBusy(false);
                return;
            }

            if (d.authorization_url) {
                if (d.callback_url) {
                    localStorage.setItem('registration_dashboard_url', d.callback_url);
                }
                window.location.href = d.authorization_url;
                return;
            }

            showToast('Unexpected response. Please try again.', 'error');
            setBusy(false);
        } catch (e) {
            showToast('Network error. Please try again.', 'error');
            setBusy(false);
        }
    };

    return (
        <div
            onClick={(e) => e.target === e.currentTarget && !busy && clearUnpaidGate()}
            style={{
                position: 'fixed',
                inset: 0,
                background: colors.scrim || 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000,
                padding: 16,
            }}
        >
            <div
                style={{
                    background: colors.raisedBg,
                    borderRadius: 16,
                    width: '100%',
                    maxWidth: 420,
                    padding: 24,
                    boxShadow: `0 20px 60px ${colors.shadowStrong || colors.shadow}`,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 12,
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <AlertCircle size={22} color={colors.warning || '#F59E0B'} />
                        <div
                            style={{
                                fontSize: 16,
                                fontWeight: 800,
                                color: colors.heading,
                            }}
                        >
                            Action locked
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={clearUnpaidGate}
                        disabled={busy}
                        aria-label="Close"
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: colors.secondaryText,
                            cursor: 'pointer',
                            padding: 4,
                        }}
                    >
                        <X size={18} />
                    </button>
                </div>
                <p
                    style={{
                        margin: '0 0 18px',
                        fontSize: 13,
                        lineHeight: 1.55,
                        color: colors.text,
                    }}
                >
                    {unpaidGate.message ||
                        'Complete your registration payment to use this feature.'}
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        type="button"
                        onClick={clearUnpaidGate}
                        disabled={busy}
                        style={{
                            flex: 1,
                            padding: '10px 12px',
                            borderRadius: 10,
                            border: `1.5px solid ${colors.border}`,
                            background: 'transparent',
                            color: colors.text,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: busy ? 'not-allowed' : 'pointer',
                            fontFamily: 'inherit',
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleComplete}
                        disabled={busy}
                        style={{
                            flex: 2,
                            padding: '10px 12px',
                            borderRadius: 10,
                            border: 'none',
                            background: colors.btnBg,
                            color: colors.btnText,
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: busy ? 'not-allowed' : 'pointer',
                            opacity: busy ? 0.7 : 1,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                            fontFamily: 'inherit',
                        }}
                    >
                        {busy ? (
                            <>
                                <Loader2
                                    size={14}
                                    style={{ animation: 'spin 1s linear infinite' }}
                                />
                                Processing…
                            </>
                        ) : (
                            <>
                                Complete payment
                                <ArrowRight size={14} />
                            </>
                        )}
                    </button>
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default CompletePaymentModal;